import { useContext, useEffect, useState } from "react";
import { Download } from "lucide-react";

import {
  ATTACHMENT_KIND_ENUM,
  INTERACTION_TYPES,
  REVIEW_STATUS_ENUM,
  SERVER_ORIGIN,
} from "../consts";
import { Context } from "../context";
import { callApi, formatDT } from "../utils";
import CommentsHistory from "../comments-history";
import ReviewStatus from "../review-status";
import VersionHistory from "../version-history";
import Filter from "./filter";
import WorkReview from "./work-review";

const getShortName = (fullName) => {
  const arr = fullName.split(" ");
  let res = arr[0];
  const getUpperFirstLetter = (word) => word.substring(0, 1).toUpperCase();
  if (arr.length > 1) {
    res += ` ${getUpperFirstLetter(arr[1])}.`;

    if (arr.length > 2) {
      res += getUpperFirstLetter(arr[2]) + ".";
    }
  }

  return res;
};

export default function StatsTab({ getGroups, group }) {
  const [stats, setStats] = useState([]);
  const [statusesSummary, setStatusesSummary] = useState([]);
  const [statsFiltered, setStatsFiltered] = useState([]);
  const [isWorkReviewOpen, setIsWorkReviewOpen] = useState(false);
  const [isCommentsHistoryOpen, setIsCommentsHistoryOpen] = useState(false);
  const [isVersionHistoryOpen, setIsVersionHistoryOpen] = useState(false);
  const [selectedStatObj, setSelectedStatObj] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState();

  const store = useContext(Context);

  const isIndividual = group.interactionType === INTERACTION_TYPES.individual;

  const getQueryParamsByInteractionType = () =>
    isIndividual
      ? { studentId: selectedStatObj?.studentId }
      : { subgroupNumber: selectedStatObj?.subgroupNumber };

  const closeWorkReview = () => setIsWorkReviewOpen(false);

  const getData = () => {
    callApi({
      method: "GET",
      path: `api/teacher/practice-groups/${group.id}/stats`,
      needAuth: true,
    }).then((response) => {
      const err = response.error;
      if (err) {
        console.error(err);
      } else {
        const respArr = response.response;
        if (respArr) {
          setStats(respArr);
        } else {
          console.error(respArr);
        }
      }
    });

    callApi({
      method: "GET",
      path: `api/teacher/practice-groups/${group.id}/stats/summary`,
      needAuth: true,
    }).then((response) => {
      const err = response.error;
      if (err) {
        console.error(err);
      } else {
        const respArr = response.response?.filter((elem) => elem.count);
        if (respArr) {
          setStatusesSummary(respArr);
        } else {
          console.error(respArr);
        }
      }
    });
  };

  useEffect(getData, [group.id]);

  const handleReviewClick = (stat) => {
    setSelectedStatObj({
      practicalNumber: stat.practicalNumber,
      studentId: stat.studentId,
      subgroupNumber: stat.subgroupNumber,
      reviewStatus: stat.reviewStatus,
      codeFileName: stat.codeFileName,
      codeLink: stat.codeLink,
      reportFileName: stat.reportFileName,
      reportLink: stat.reportLink,
      isOverdue: (stat.reworkEndDate ?? "") < (store?.today ?? ""),
    });
    setIsWorkReviewOpen(true);
  };

  const handleCommentsClick = (stat) => {
    setSelectedStatObj({
      practicalNumber: stat.practicalNumber,
      studentId: stat.studentId,
      subgroupNumber: stat.subgroupNumber,
    });
    setIsCommentsHistoryOpen(true);
  };

  const onChangeReviewStatus = (code) => {
    if (code) {
      const res = statusesSummary.find((elem) => elem.code === code);
      setSelectedStatus(res);
    } else {
      setSelectedStatus(undefined);
    }
  };

  const getQueryParams = () =>
    new URLSearchParams(getQueryParamsByInteractionType());

  const getBody = (body) => JSON.stringify(body);

  const onUpdateStatus = (body) => {
    callApi({
      method: "PUT",
      path: `api/teacher/practice-groups/${group.id}/works/${selectedStatObj?.practicalNumber}/review`,
      body: getBody(body),
      needAuth: true,
      queryParams: getQueryParams(),
    }).then(() => {
      closeWorkReview();
      getData();
      getGroups();
    });
  };

  const onAddComment = (body) => {
    callApi({
      method: "POST",
      path: `api/teacher/practice-groups/${group.id}/works/${selectedStatObj?.practicalNumber}/comments`,
      body: getBody(body),
      needAuth: true,
      queryParams: getQueryParams(),
    }).then(() => {
      closeWorkReview();
      getData();
    });
  };

  const openVersionHistory = (stat, kind) => {
    setSelectedStatObj({
      practicalNumber: stat.practicalNumber,
      studentId: stat.studentId,
      subgroupNumber: stat.subgroupNumber,
      kind,
    });
    setIsVersionHistoryOpen(true);
  };

  const renderLink = (stat, kind, pathName, label) => {
    if (pathName) {
      return (
        <div className="attachment-container">
          <a className="link" href={`${SERVER_ORIGIN}${pathName}`}>
            <Download size={12} /> {label}
          </a>
          <button
            className="btn tiny"
            onClick={() => openVersionHistory(stat, kind)}
          >
            История
          </button>
        </div>
      );
    }
    return <span className="muted small">не размещён</span>;
  };

  const renderCommonColumns = (stat) => (
    <>
      <td className="num-cell">{stat.practicalNumber}</td>
      <td className="num-cell">{stat.variant}</td>
      <td>
        {renderLink(
          stat,
          ATTACHMENT_KIND_ENUM.report,
          stat.reportLink,
          `Отчёт v${stat.reportVersion}`,
        )}
      </td>
      <td>
        {renderLink(
          stat,
          ATTACHMENT_KIND_ENUM.code,
          stat.codeLink,
          `Код v${stat.codeVersion}`,
        )}
      </td>
      <td>
        <div className="review-status-containter">
          <ReviewStatus
            color={stat.reviewStatus?.color}
            name={stat.reviewStatus?.name}
          />
          {stat.newVersion && <span className="tag">Новая версия</span>}
        </div>
      </td>
      <td>
        <button className="btn tiny" onClick={() => handleReviewClick(stat)}>
          Проверить
        </button>
      </td>
      <td>{formatDT(stat.postedAt)}</td>
      <td>
        {!!stat.commentsCount && (
          <div className="comment-column">
            <div>
              <div>Всего: {stat.commentsCount}</div>
              <div>Последний: {formatDT(stat.lastCommentAt)}</div>
            </div>
            <button
              className="btn tiny"
              onClick={() => handleCommentsClick(stat)}
            >
              Открыть
            </button>
          </div>
        )}
      </td>
    </>
  );

  let type = "";
  let headerList = [];
  let renderRow = () => {};

  const getRowClassName = (reviewStatus) =>
    reviewStatus?.code === REVIEW_STATUS_ENUM.onReview ? "on-review" : "";

  if (isIndividual) {
    type = "индивидуальный";
    headerList = ["ФИО"];

    renderRow = (s, i) => (
      <tr key={i} className={getRowClassName(s.reviewStatus)}>
        <td>{s.fullName}</td>
        {renderCommonColumns(s)}
      </tr>
    );
  } else {
    type = "групповой";
    headerList = ["Подгруппа", "Состав"];

    renderRow = (s, i) => (
      <tr key={i} className={getRowClassName(s.reviewStatus)}>
        <td>Подгруппа {s.subgroupNumber}</td>
        <td className="small">
          {s.subgroupMembers.map(getShortName).join(", ")}
        </td>
        {renderCommonColumns(s)}
      </tr>
    );
  }
  headerList = headerList.concat([
    "Практика",
    "Вариант",
    "Отчёт",
    "Исходный код",
    "Статус проверки",
    "Действия",
    "Размещено",
    "Комментарии",
  ]);

  return (
    <div className="card">
      <h4>Статистика выполнения — {type} формат</h4>
      <Filter
        stats={stats}
        setStatsFiltered={setStatsFiltered}
        isIndividual={isIndividual}
        selectedStatus={selectedStatus}
        statusesSummary={statusesSummary}
        onSelectedStatusChange={onChangeReviewStatus}
      />
      <div className="review-statuses-summary">
        {statusesSummary.map((elem) => {
          return (
            <button
              key={elem.code}
              className={`btn small${selectedStatus?.code === elem.code ? " active" : ""}`}
              onClick={() =>
                setSelectedStatus(
                  selectedStatus?.code === elem.code ? undefined : elem,
                )
              }
            >
              {`${elem.name}: ${elem.count}`}
            </button>
          );
        })}
      </div>
      <table className="data-table">
        <thead>
          <tr>
            {headerList.map((elem, i) => (
              <th key={i}>{elem}</th>
            ))}
          </tr>
        </thead>
        <tbody>{statsFiltered.map(renderRow)}</tbody>
      </table>
      <p className="muted small">
        Обновляется автоматически, не реже одного раза в сутки.
      </p>
      {isWorkReviewOpen && (
        <WorkReview
          codeFileName={selectedStatObj?.codeFileName}
          codeLink={selectedStatObj?.codeLink}
          isOverdue={selectedStatObj?.isOverdue}
          reportFileName={selectedStatObj?.reportFileName}
          reportLink={selectedStatObj?.reportLink}
          savedReviewStatus={selectedStatObj?.reviewStatus}
          onCancel={closeWorkReview}
          onAddComment={(body) => onAddComment(body)}
          onUpdateStatus={(body) => onUpdateStatus(body)}
        />
      )}
      {isCommentsHistoryOpen && (
        <CommentsHistory
          apiPath={`api/teacher/practice-groups/${group.id}/works/${selectedStatObj?.practicalNumber}/comments`}
          apiQueryParams={getQueryParamsByInteractionType()}
          onCancel={() => setIsCommentsHistoryOpen(false)}
        />
      )}
      {isVersionHistoryOpen && (
        <VersionHistory
          apiPath={`api/teacher/practice-groups/${group.id}/works/${selectedStatObj?.practicalNumber}/versions`}
          apiQueryParams={{
            kind: selectedStatObj.kind,
            ...getQueryParamsByInteractionType(),
          }}
          onCancel={() => setIsVersionHistoryOpen(false)}
        />
      )}
    </div>
  );
}
